import { SyncStateMachine } from './sync-state-machine';
import type { SyncState, SyncTransitionListener } from './types';

/**
 * Singleton service that manages one SyncStateMachine per vault.
 *
 * Each vault has its own independent state machine. This service is the
 * single source of truth for sync status across the app.
 *
 * Usage:
 *   const machine = syncMachineService.getOrCreate('vault-abc');
 *   machine.initialiseSyncEnabled({ ... });
 *
 * Pattern mirrors CryptographerService — class singleton, exposed via a
 * module-level instance.
 */
class SyncMachineService {
  private static instance: SyncMachineService;
  private machines: Map<string, SyncStateMachine> = new Map();
  private globalListeners: Set<SyncTransitionListener> = new Set();

  private constructor() {}

  static getInstance(): SyncMachineService {
    if (!SyncMachineService.instance) {
      SyncMachineService.instance = new SyncMachineService();
    }
    return SyncMachineService.instance;
  }

  /**
   * Return the machine for the given vault ID, creating one in
   * `InitialisingState` if it does not yet exist.
   */
  getOrCreate(vaultId: string): SyncStateMachine {
    let machine = this.machines.get(vaultId);
    if (!machine) {
      machine = new SyncStateMachine(vaultId);
      // Attach any global listeners registered before this machine was created
      for (const listener of this.globalListeners) {
        machine.on(listener);
      }
      this.machines.set(vaultId, machine);
    }
    return machine;
  }

  /**
   * Return the machine for the given vault ID, or `undefined` if it has
   * not been created yet.  Prefer `getOrCreate` in most cases.
   */
  get(vaultId: string): SyncStateMachine | undefined {
    return this.machines.get(vaultId);
  }

  /**
   * Remove and destroy the machine for the given vault ID.
   * Call this when a vault is deleted locally.
   */
  remove(vaultId: string): void {
    this.machines.delete(vaultId);
  }

  /**
   * Return all active machines as an array of `[vaultId, machine]` pairs.
   */
  listAll(): Array<[string, SyncStateMachine]> {
    return Array.from(this.machines.entries());
  }

  /**
   * Return the current state for every active machine.
   * Useful for iterating all vaults during a polling cycle.
   */
  getAllStates(): Array<{ vaultId: string; state: SyncState }> {
    return Array.from(this.machines.entries()).map(([vaultId, machine]) => ({
      vaultId,
      state: machine.state,
    }));
  }

  /**
   * Subscribe a listener to every machine — both existing and any created
   * in the future.  Returns an unsubscribe function.
   *
   * Useful for global transition logging in development (Phase 1.5).
   */
  onAny(listener: SyncTransitionListener): () => void {
    this.globalListeners.add(listener);

    // Subscribe to already-existing machines
    const unsubscribers: Array<() => void> = [];
    for (const machine of this.machines.values()) {
      unsubscribers.push(machine.on(listener));
    }

    return () => {
      this.globalListeners.delete(listener);
      unsubscribers.forEach((unsub) => unsub());
    };
  }

  /**
   * Remove all machines. Intended for testing or full logout scenarios.
   */
  clear(): void {
    this.machines.clear();
    this.globalListeners.clear();
  }
}

/** Module-level singleton — import this directly rather than calling `getInstance()`. */
export const syncMachineService = SyncMachineService.getInstance();

export { SyncMachineService };
